import "./edit-panel.scss";

import * as _ from "lodash";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import { connect } from "react-redux";

import actions from "@/actions";
import { ConcealablePanel, Tabs } from "@/components";
import type { Light } from "@/models";

import { Flags, General, Overview } from "./tabs";

type EditableLight = Light & { oldValue?: unknown; changed?: boolean };

/**
 * Props for the EditPanel component.
 */
interface EditPanelProps {
    /**
     * Every light in the active history scope, used for overlap detection.
     */
    lights: Light[];
    /**
     * The lights currently selected on the map.
     */
    selectedLights: Light[];
    /**
     * The toast notification callback.
     */
    toastr: (message: string, type?: string) => void;
    /**
     * Callback committing light changes to history.
     */
    persistLights: (historyStateProp: string, ...args: unknown[]) => void;
    /**
     * Callback applying light changes without committing them to history.
     */
    changeLights: (historyStateProp: string, lights: EditableLight[]) => void;
    /**
     * The name of the current map mode.
     */
    mode: string;
}

const EditPanelComponent = ({
    lights,
    selectedLights,
    toastr,
    persistLights,
    changeLights: changeLightsAction,
    mode,
}: EditPanelProps): ReactNode => {
    const [shouldPersist, setShouldPersist] = useState(false);

    const changeLights = useCallback(
        (field: string, value: unknown): EditableLight[] => {
            const historyStateProp = mode === "view" ? "global" : "mode";
            const changedLights: EditableLight[] = [];
            _.each(selectedLights, (selectedLight) => {
                const light: EditableLight = _.cloneDeep(selectedLight);
                light.oldValue = _.get(light, field);
                _.set(light, field, value);
                light.changed = true;
                changedLights.push(light);
            });
            const IsOverlapping = _.some(changedLights, (selectedLight) =>
                selectedLight.isOverlapping([...lights, ...changedLights]),
            );

            if (IsOverlapping) {
                toastr(
                    "Warning: There are overlapping fixtures on the map. You may ignore this message if overlapping is expected.",
                    "warning",
                );
            }
            changeLightsAction(historyStateProp, changedLights);

            return changedLights;
        },
        [mode, selectedLights, lights, toastr, changeLightsAction],
    );

    const createTabs = useCallback(() => {
        const historyStateProp = mode === "view" ? "global" : "mode";
        const tabProps = {
            lights: selectedLights,
            persistLights: (...args) => {
                if (shouldPersist || args.length > 0) {
                    setShouldPersist(false);
                    persistLights(historyStateProp, ...args);
                }
            },
            changeLights: (field: string, value: unknown) => {
                if (!shouldPersist) {
                    setShouldPersist(true);
                }
                return changeLights(field, value);
            },
        };

        return [
            Tabs.createTab("Overview", Overview, tabProps),
            Tabs.createTab("General", General, tabProps),
            Tabs.createTab("Flags", Flags, tabProps),
        ];
    }, [mode, selectedLights, shouldPersist, persistLights, changeLights]);

    const tabs = createTabs();

    return (
        <ConcealablePanel className="edit-tab" title="Edit">
            <Tabs>{tabs}</Tabs>
        </ConcealablePanel>
    );
};

const mapStateToProps = ({ map }) => ({
    mode: map.general.mode.name,
    lights:
        map.general.mode.name === "view"
            ? _.values(_.cloneDeep(map.values.global.present.lights))
            : _.values(_.cloneDeep(map.values.mode.present.lights)),
});

const mapDispatchToProps = {
    toastr: actions.window.showMessage,
    changeLights: (mode, lights) =>
        actions.map.values[mode].setLights(lights, false),
    persistLights: (mode, ...args) =>
        actions.map.values[mode].updateLights(...args),
};

export const EditPanel = connect(
    mapStateToProps,
    mapDispatchToProps,
)(EditPanelComponent);
